import moment from 'moment'
import React, { useEffect } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { compose } from 'recompose'

import { DEFAULT_DATE_FORMAT } from 'constants/date'
import { Administration } from 'services/administrations/models'
import { Child } from 'services/child/constants'
import { FREQUENCIES, FREQUENCY_LABELS } from 'services/medicationForms/constants'

import { isSameDay } from 'utils/date'

import { withAppService, withAppServiceProps } from 'services/app'
import appSelectors from 'services/app/selectors'
import { withDailyDiaryMedicationsService, withDailyDiaryMedicationsServiceProps } from 'services/dailyDiaryMedications'
import { getDailyDiaryMedicationsListCriteria } from 'services/dailyDiaryMedications/selectors'
import * as dailyDiaryMedicationsSelectors from 'services/dailyDiaryMedications/selectors'
import { withRegisterService, withRegisterServiceProps } from 'services/legacy/register'
import { withRouter, withRouterProps } from 'services/router'
import { withModalService, withModalServiceProps } from 'services/utils/modal'
import { withPaginationUtils, withPaginationUtilsProps } from 'services/utils/pagination'

import { TimeString } from 'components'
import { ModalType } from 'modals'

import { RootState } from 'core/reducers'

import MedicationView from './MedicationsView'

export interface AdministerParams {
  administration?: Administration
  child?: Child
}

const DAILY_DIARY_MEDICATIONS_GROUPS = {
  read: [
    'childRegisterDiaryRecordsResult.childRegister',
    'childRegisterDiaryRecordsResult.diaryRecords',
    'child',
    'administration',
    'administration.medicationForm',
    'administration.medicationDetails',
    'medicationForm',
    'medicationForm.medicationDetails',
    'medicationForm:medicationDetails',
    'medicationDetails',
  ],
}

type MedicationsContainerProps = withAppServiceProps
  & withModalServiceProps
  & withPaginationUtilsProps
  & withRegisterServiceProps
  & withDailyDiaryMedicationsServiceProps
  & withRouterProps

const mapState = (state: RootState, {
  dailyDiaryMedicationsListState,
}: MedicationsContainerProps) => ({
  dailyDiaryMedicationList: dailyDiaryMedicationsSelectors.getDailyDiaryActiveMedicationsList(state),
  errorMessages: appSelectors.getErrorMessages(dailyDiaryMedicationsListState),
  isFetching: appSelectors.getIsFetching(dailyDiaryMedicationsListState),
})

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

const MedicationsContainer: React.FC<MedicationsContainerProps & PropsFromRedux> = ({
  dailyDiaryMedicationList,
  dailyDiaryMedicationsActions,
  errorMessages,
  isFetching,
  location,
  modalActions,
  registerState,
}) => {
  const query = location?.query
  const isCurrentDate = isSameDay(query?.date, undefined)

  const fetchData = () => {
    const criteria = getDailyDiaryMedicationsListCriteria({
      entryDate: query?.date || moment().format(DEFAULT_DATE_FORMAT),
      room: query?.room,
      timeType: query?.timeType,
    })

    dailyDiaryMedicationsActions.clearList()
    dailyDiaryMedicationsActions.list({
      mergeResult: true,
      params: {
        criteria,
        groups: DAILY_DIARY_MEDICATIONS_GROUPS,
        page: 1,
      },
      recursively: true,
    })
  }

  useEffect(() => {
    fetchData()

    return () => {
      dailyDiaryMedicationsActions.clearList()
    }
  }, [query])

  const handleAdministerClick = ({
    administration,
    child,
  }: AdministerParams = {}) => {
    modalActions.show<ModalType.ADD_ADMINISTRATION>(ModalType.ADD_ADMINISTRATION, {
      onSubmitSuccess: fetchData,
      params: {
        childOption: child ? {
          avatar: child.photo,
          label: child.name,
          value: child.id,
        } : undefined,
        doseOption: administration ? {
          label: (
            <React.Fragment>
              {administration.medicationForm?.medicationDetails?.frequency === FREQUENCIES.AS_NEEDED
                ? FREQUENCY_LABELS[FREQUENCIES.AS_NEEDED]
                : <TimeString date={administration.timeOfAdministration} local={false} />}
              {', '}
              {administration.medicationForm?.medicationDetails?.dose}
            </React.Fragment>
          ),
          value: administration.id,
        } : undefined,
        medicineOption: administration ? {
          label: administration?.medicationForm?.medicationDetails?.medication,
          value: administration?.medicationForm?.id,
        } : undefined,
      },
    }, {
      enableMultipleModal: true,
    })
  }

  const handleParentSignatureClick = (id) => {
    modalActions.show<ModalType.ADMINISTRATION_PARENT_SIGNATURE>(ModalType.ADMINISTRATION_PARENT_SIGNATURE, {
      id,
      onSubmitSuccess: fetchData,
    })
  }

  const handleEmergencyDoseClick = (child?: Child) => {
    modalActions.show<ModalType.ADD_EMERGENCY_DOSE>(ModalType.ADD_EMERGENCY_DOSE, {
      childOption: child ? {
        avatar: child.photo,
        label: child.name,
        value: child.id,
      } : undefined,
      date: query?.date,
      isDiaryContext: true,
      onSubmitSuccess: fetchData,
    }, {
      enableMultipleModal: true,
    })
  }

  return (
    <MedicationView
      dailyDiaryMedicationList={dailyDiaryMedicationList}
      errorMessages={errorMessages}
      isCurrentDate={isCurrentDate}
      isLoading={isFetching}
      registers={registerState?.data}
      onAdministerClick={handleAdministerClick}
      onEmergencyDoseClick={handleEmergencyDoseClick}
      onParentSignatureClick={handleParentSignatureClick}
    />
  )
}

const enhance = compose(
  withAppService,
  withModalService,
  withPaginationUtils,
  withRegisterService,
  withDailyDiaryMedicationsService,
  withRouter,
  connector,
)

export default enhance(MedicationsContainer)
